import * as S from "../../ConvenienceTest/ConvenienceTest.style";
import { useEffect, useRef, useState } from "react";
import Select from "react-dropdown-select";
import { Attachment } from "../../Attachment";
import { Spinner, SpinnerSize, TextField } from "@fluentui/react";
import { InputStyles } from "../../GlobalStyle";
import DocumentIntelligenceService from "../../../services/DocumentIntelligenceService";
import moment from "moment";
import { currencyFormatter } from "../../../utils/numberFormatter";
import { parseToInteger } from "../../../utils/UserIdentity";
import { ClaimMessageData } from "../../ConvenienceTest/ConvenienceTestScreenAccountHolderData/ClaimMessage/ClaimMessageType";
import Attachments from "../../../services/AttachmentService";

interface SelectOption {
  key: string;
  text: string;
  name: string;
}
interface FinancialDataProps {
  handleFinancialChange: (event: any) => void;
  userIdentity: any;
  data: any;
  jobs: SelectOption[];
  birthdate: string;
  blockedByAuthorizationWarning: boolean;
  financialFormVal: Record<string, boolean>;
  setFinancialFormVal: (name: string, value: boolean) => void;
  isCalculatedMaxContribution: (value: boolean) => void;
  handleCreateFinancialClaimMessage: (
    claim: ClaimMessageData | null,
    idToRemove?: string
  ) => void;
}

const FinancialData: React.FC<FinancialDataProps> = ({
  handleFinancialChange,
  data,
  financialFormVal,
  setFinancialFormVal,
  jobs,
  userIdentity,
  birthdate,
  blockedByAuthorizationWarning,
  isCalculatedMaxContribution,
  handleCreateFinancialClaimMessage,
}) => {
  const {
    economicActivity,
    proofOfIncome,
    gafiQuestion,
    publicPosition,
    maxContribution,
  } = data;

  const [errors, setErrors] = useState({
    economicActivity: "",
    proofOfIncome: "",
    gafiQuestion: "",
    publicPosition: "",
    maxContribution: "",
  });

  const maxContributionRef = useRef<any>();
  const [displayedMaxContribution, setDisplayedMaxContribution] = useState("0");

  // proofOfIncome
  const extractFileNameFromUrl = (fileUrl: string): string => {
    if (!fileUrl || !fileUrl.startsWith("https://")) {
      console.warn("La URL no es válida o no comienza con https://");
      return "";
    }
    try {
      const urlObj = new URL(fileUrl);
      return urlObj.pathname.split("/").pop() || "";
    } catch (error) {
      console.error("URL inválida:", fileUrl);
      return "";
    }
  };
  const [proofOfIncomeDocument, setProofOfIncomeDocument] =
    useState<File | null>(null);
  const [isLoadingProofOfIncome, setIsLoadingProofOfIncome] = useState(false);

  const [proofOfIncomeFileName, setProofOfIncomeFileName] =
    useState<string>(proofOfIncome);

  useEffect(() => {
    if (proofOfIncome && typeof proofOfIncome === "string") {
      const fileName = extractFileNameFromUrl(proofOfIncome);
      if (fileName) {
        setProofOfIncomeFileName(fileName);
      }
    }
  }, [proofOfIncome]);

  const handleMaxContributionChange = () => {
    console.log("handleMaxContributionChange >>> ", maxContributionRef.current);
    handleInputChange({
      target: {
        name: "maxContribution",
        value: maxContributionRef.current
          ? parseToInteger(maxContributionRef.current)
          : 0,
      },
    });
  };

  useEffect(() => {
    const analyzeProofOfIncome = async () => {
      if (
        proofOfIncome &&
        typeof proofOfIncome === "string" &&
        maxContribution === null &&
        !isLoadingProofOfIncome
      ) {
        handleCreateFinancialClaimMessage({
          id: "info-loading-initial-proofOfIncome",
          type: "warning",
          message:
            "Estamos calculando tu aportación máxima a partir de tu justificante de ingresos. Por favor espera...",
        });
        setIsLoadingProofOfIncome(true);
        isCalculatedMaxContribution(false);

        try {
          // 1. Obtener el blob desde el backend
          const response = await Attachments.getBlobAttachment(proofOfIncome);

          // 2. Convertir blob a File
          const file = new File([response.data], proofOfIncome, {
            type: response.headers["content-type"] || "application/pdf",
          });

          // 3. Enviar al servicio de verificación
          const verification =
            await DocumentIntelligenceService.sendDocumentToVerifyWithOrigenFondos(
              {
                proofOfIncome: file,
              }
            );

          if (verification?.status === 200 && verification.data) {
            const { importeNetoMes, baseLiquidableGeneral } = verification.data;

            let importeStr = importeNetoMes?.replace(".", "") ?? "0";
            if (importeStr !== "0") {
              importeStr = importeStr.replace(",", ".");
            }
            const importe = parseFloat(importeStr);

            let baseStr = baseLiquidableGeneral?.replace(".", "") ?? "0";
            if (baseStr !== "0") {
              baseStr = baseStr.replace(",", ".");
            }
            const base = parseFloat(baseStr || 0);
            const ageInput = moment().diff(moment(birthdate), "years");

            let maxContribution = 0;

            if (importe > 0) {
              maxContribution = importe * 0.15 * (ageInput - 18) * 12;
            } else if (base > 0) {
              maxContribution = (base / 12) * 0.15 * (ageInput - 18);
            }

            setDisplayedMaxContribution(
              currencyFormatter.format(maxContribution)
            );
            maxContributionRef.current = maxContribution.toString();

            handleInputChange({
              target: {
                name: "maxContribution",
                value: maxContribution,
              },
            });

            handleCreateFinancialClaimMessage({
              id: "success-loading-initial-proofOfIncome",
              type: "success",
              message:
                "Documento de prueba de ingresos analizado correctamente y aportación máxima justificada calculada. ",
            });
          } else {
            console.warn(
              "Documento no fue analizado correctamente o respuesta inesperada",
              verification
            );
          }
        } catch (error: any) {
          console.error(
            "Error al analizar documento desde base de datos:",
            error
          );
          if (error.response) {
            console.error("Respuesta del servidor:", error.response.data);
          }
        } finally {
          setIsLoadingProofOfIncome(false);
          isCalculatedMaxContribution(true);
          handleCreateFinancialClaimMessage(
            null,
            "info-loading-initial-proofOfIncome"
          );
        }
      }
    };

    analyzeProofOfIncome();
  }, [proofOfIncome, maxContribution, isLoadingProofOfIncome]);

  /*************************************************
   * MAXCONTRIBUTION
   *************************************************/
  useEffect(() => {
    if (userIdentity && userIdentity.maxContribution) {
      setDisplayedMaxContribution(
        currencyFormatter.format(userIdentity.maxContribution)
      );
    }
  }, [userIdentity]);

  const getErrorMessage = (field: string, value: any) => {
    switch (field) {
      case "economicActivity":
        return !value.trim() ? (
          <S.Error>El campo Actividad es obligatorio</S.Error>
        ) : (
          ""
        );

      case "proofOfIncome":
        return value.size === 0 ? (
          <S.Error>El campo Justificante de ingesos es obligatorio</S.Error>
        ) : (
          ""
        );

      case "gafiQuestion":
        return value === "true" ? (
          <S.Error>Ninguna pregunta puede quedar sin contestar</S.Error>
        ) : (
          ""
        );

      case "publicPosition":
        return value === "true" ? (
          <S.Error>Ninguna pregunta puede quedar sin contestar</S.Error>
        ) : (
          ""
        );

      case "maxContribution":
        return typeof value !== "number" || isNaN(value) || value < 0 ? (
          <S.Error>
            El campo Aportación máxima justificada es obligatorio
          </S.Error>
        ) : (
          ""
        );
      default:
        throw new Error("Error en el switch financiero.");
    }
  };

  const getFinancialPlainErrorMessage = (field: string): string => {
    switch (field) {
      case "economicActivity":
        if (getIsMinor(birthdate)) {
          return "";
        }
        return "El campo Actividad es obligatorio para mayores de edad";
      case "proofOfIncome":
        if (getIsMinor(birthdate)) {
          return "";
        }
        return "El campo Justificante de ingesos es obligatorio";
      case "gafiQuestion":
        return "";
      case "publicPosition":
        return "";
      case "maxContribution":
        return "";
      default:
        return "";
    }
  };

  useEffect(() => {
    Object.entries(data).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === false
      ) {
        handleCreateFinancialClaimMessage({
          id: key,
          type: "error",
          message: getFinancialPlainErrorMessage(key),
        });
      }
    });
  }, []);

  useEffect(() => {
    Object.entries(data).forEach(([key, value]) => {
      const isEmpty =
        value === null ||
        value === undefined ||
        value === "" ||
        value === false;
      const message = getFinancialPlainErrorMessage(key);
      if (isEmpty && message) {
        handleCreateFinancialClaimMessage({ id: key, type: "error", message });
      } else {
        handleCreateFinancialClaimMessage(null, key);
      }
    });
  }, [data]);

  const calculateMaxContribution = (
    importe: number,
    base: number,
    birthdate: string
  ): number => {
    const ageInput = moment().diff(moment(birthdate), "years");
    if (importe > 0) return importe * 0.15 * (ageInput - 18) * 12;
    if (base > 0) return (base / 12) * 0.15 * (ageInput - 18);
    return 0;
  };

  const getIsMinor = (birthdate: string): boolean => {
    const age = moment().diff(moment(birthdate), "years");
    return age < 18;
  };

  const handleInputChange = (event: any) => {
    console.log(
      "handleInputChange >>> ",
      event.target.name,
      event.target.value
    );
    if (event.target !== undefined) {
      var { name, value } = event.target;
      var error = getErrorMessage(name, value);
      setErrors((prevState: any) => {
        return {
          ...prevState,
          [name]: error,
        };
      });
      setFinancialFormVal(name, error === "");
      handleFinancialChange(event);
    }
  };

  const setProofOfIncome = (event: any) => {
    if (event.target !== undefined) {
      var { name, value } = event.target;
      var error = getErrorMessage(name, value);
      setErrors((prevState: any) => {
        return {
          ...prevState,
          [name]: error,
        };
      });
      setFinancialFormVal(name, error === "");
      handleFinancialChange(event);
    }
  };

  const handleProofOfIncomeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsLoadingProofOfIncome(true);
    isCalculatedMaxContribution(false);
    handleCreateFinancialClaimMessage({
      id: "loading-proofOfIncome",
      type: "warning",
      message:
        "Estamos calculando tu aportación máxima a partir de tu justificante de ingresos. Por favor espera...",
    });
    switch (event.target.id) {
      case "proofOfIncome":
        setProofOfIncomeDocument(event.target.files?.[0] || null);
        break;
      default:
        break;
    }

    if (event.target.files && event.target.files.length > 0) {
      const documentData = {
        proofOfIncome: event.target.files[0],
      };

      try {
        const response =
          await DocumentIntelligenceService.sendDocumentToVerifyWithOrigenFondos(
            documentData
          );

        console.log("response DocumentIntelligenceService >>> ", response);

        if (response && response.status === 200 && response.data) {
          const { fileName, importeNetoMes, baseLiquidableGeneral } =
            response.data;

          const importe = parseFloat(importeNetoMes || 0);
          const base = parseFloat(baseLiquidableGeneral || 0);

          // calcular la edad a partir de la fecha de nacimiento con la libreria moment
          const maxContribution = calculateMaxContribution(
            importe,
            base,
            birthdate
          );

          setDisplayedMaxContribution(
            currencyFormatter.format(maxContribution)
          );
          maxContributionRef.current = maxContribution.toString();

          handleInputChange({
            target: {
              name: "maxContribution",
              value: maxContribution,
            },
          });

          setProofOfIncome(event);
          handleCreateFinancialClaimMessage({
            id: "success-proofOfIncome",
            type: "success",
            message: `Documento de prueba de ingresos analizado correctamente. Aportación máxima justificada calculada.`,
          });
        } else {
          console.warn(
            "Documento no fue analizado correctamente o respuesta inesperada",
            response
          );
          // Mostrar mensaje de error al usuario
          setProofOfIncome(null);
          handleInputChange({
            target: {
              name: "maxContribution",
              value: 0,
            },
          });
        }
      } catch (error: any) {
        console.error("Error al analizar el documento:", error);
        setProofOfIncome(null);
        handleInputChange({
          target: {
            name: "maxContribution",
            value: 0,
          },
        });

        if (error.response) {
          console.error("Respuesta del servidor:", error.response.data);
        }
      } finally {
        setIsLoadingProofOfIncome(false);
        isCalculatedMaxContribution(true);
        handleCreateFinancialClaimMessage(null, "loading-proofOfIncome");
      }
    }
  };

  return (
    <>
      <S.FieldSet>
        <S.FieldRSCont className="full-width">
          <S.DropDownLabel>Actividad *</S.DropDownLabel>
          <Select
            name="economicActivity"
            className="economicActivity-select"
            labelField="name"
            options={jobs}
            onChange={(event: any) =>
              handleInputChange({
                target: { name: "economicActivity", value: event[0].key },
              })
            }
            // @ts-ignore
            value={economicActivity}
            disabled={blockedByAuthorizationWarning}
            placeholder="Selecciona una actividad"
            dropdownHeight="2000"
            searchable={true}
            searchBy={"name"}
            noDataLabel={"No encontrado"}
            backspaceDelete={true}
            valueField={"key"}
            values={jobs.filter(
              (economicActivityOption: SelectOption) =>
                economicActivityOption.key === economicActivity
            )}
          />
          {errors.economicActivity !== "" && errors.economicActivity}
        </S.FieldRSCont>
        <S.FieldRBCont>
          <S.Label>Justificante de ingresos *</S.Label>
          <S.FieldFileCont>
            <S.FileInput
              id="proofOfIncome"
              name="proofOfIncome"
              type="file"
              accept=".png, .jpg, .pdf, .jpeg"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleProofOfIncomeChange(event)
              }
              disabled={isLoadingProofOfIncome || blockedByAuthorizationWarning}
              required
            />
          </S.FieldFileCont>
          <p style={{ fontSize: "11px", margin: "4px 0px" }}>
            <span>
              * Se admiten los siguientes formatos de imagen: .png, .jpg, .jpeg
              y .pdf
            </span>
          </p>
          {errors.proofOfIncome !== "" && errors.proofOfIncome}
        </S.FieldRBCont>
        <S.FieldRBCont>
          <S.Label
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: "8px" }}>
              Aportación Máxima Justificada
            </span>
            {isLoadingProofOfIncome && (
              <Spinner size={SpinnerSize.small} className="button-spinner" />
            )}
          </S.Label>
          <TextField
            // @ts-ignore
            styles={{
              ...InputStyles,
              root: {
                ...InputStyles.root,
                width: "100%",
                marginBottom: "0px",
              },
            }}
            id="maxContribution"
            name={"maxContribution"}
            type="text"
            value={displayedMaxContribution}
            onChange={(event: any) => {
              maxContributionRef.current = currencyFormatter.format(
                event.target.value
              );
              setDisplayedMaxContribution(
                currencyFormatter.format(maxContribution)
              );
              handleMaxContributionChange();
            }}
            disabled={true}
            style={{
              color: "#485354b5",
              textAlign: "end",
              width: "100%",
              top: "-1px",
            }}
          />
          <p style={{ fontSize: "11px", margin: "4px 0px" }}>
            <span>
              * Importe máximo que puedes justificar como aportación económica,
              calculado automáticamente a partir de la información recogida en
              tus documentos (como tu sueldo o tu base imponible) y tu edad.
            </span>
          </p>
        </S.FieldRBCont>
      </S.FieldSet>

      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-md12">
          {proofOfIncome && typeof proofOfIncome === "string" && (
            <div
              className="document-preview-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <S.DocumentAttachmentWrapperFullWidth>
                <Attachment
                  fileName={proofOfIncomeFileName}
                  className="document-preview"
                />
              </S.DocumentAttachmentWrapperFullWidth>
            </div>
          )}
        </div>
      </div>
      <div className="ms-Grid-row">
        <div className="ms-Grid-col ms-md6">
          <S.RadioCont>
            <S.Question>
              ¿Alguno de los intervinientes o sus familiares realiza alguna
              actividad o es residente/nacional de alguna de las jurisdicciones
              consideradas de riesgo de acuerdo con la normativa de prevención
              de blanqueo de capitales o no cooperantes según GAFI (Afganistán,
              Corea del Norte, Irán, Laos, Papúa Nueva Guinea, Uganda, Yemen,
              Bosnia y Herzegovina, Guyana, Irak, mi an-Mar, Siria y Vanuatu)?
            </S.Question>
            <S.RadioSelect>
              <S.RadioLabel>
                Si
                <S.RadioInput
                  id="gafiQuestion"
                  type="radio"
                  name="gafiQuestion"
                  value="true"
                  onChange={(event: any) =>
                    handleInputChange({
                      target: { name: "gafiQuestion", value: true },
                    })
                  }
                  disabled={blockedByAuthorizationWarning}
                  checked={gafiQuestion === true}
                />
                <S.RadioCheck />
              </S.RadioLabel>
            </S.RadioSelect>
            <S.RadioSelect>
              <S.RadioLabel>
                No
                <S.RadioInput
                  id="gafiQuestion"
                  type="radio"
                  name="gafiQuestion"
                  value="false"
                  onChange={(event: any) =>
                    handleInputChange({
                      target: { name: "gafiQuestion", value: false },
                    })
                  }
                  disabled={blockedByAuthorizationWarning}
                  checked={gafiQuestion === false}
                />
                <S.RadioCheck />
              </S.RadioLabel>
            </S.RadioSelect>
            {errors.gafiQuestion !== "" && errors.gafiQuestion}
          </S.RadioCont>
        </div>
        <div className="ms-Grid-col ms-md6">
          <S.RadioCont>
            <S.Question>
              ¿Alguno de los intervinientes o sus familiares son personas
              políticamente expuestas (personas que desempeñan o han desempeñado
              funciones públicas relevantes en los últimos años)?
            </S.Question>
            <S.RadioSelect>
              <S.RadioLabel>
                Sí
                <S.RadioInput
                  id="publicPosition"
                  type="radio"
                  name="publicPosition"
                  value="true"
                  onChange={() =>
                    handleInputChange({
                      target: { name: "publicPosition", value: true },
                    })
                  }
                  disabled={blockedByAuthorizationWarning}
                  checked={publicPosition === true}
                />
                <S.RadioCheck />
              </S.RadioLabel>
            </S.RadioSelect>

            <S.RadioSelect>
              <S.RadioLabel>
                No
                <S.RadioInput
                  id="publicPosition"
                  type="radio"
                  name="publicPosition"
                  value="false"
                  disabled={blockedByAuthorizationWarning}
                  onChange={() =>
                    handleInputChange({
                      target: { name: "publicPosition", value: false },
                    })
                  }
                  checked={publicPosition === false}
                />
                <S.RadioCheck />
              </S.RadioLabel>
            </S.RadioSelect>

            {errors.publicPosition !== "" && errors.publicPosition}
          </S.RadioCont>
        </div>
      </div>
    </>
  );
};

export default FinancialData;
